// Español neutro.
//
// ═══════════════════════════════════════════════════════════════════
// QUÉ QUIERE DECIR "NEUTRO" ACÁ, Y QUÉ SE PERDIÓ AL ELEGIRLO
// ═══════════════════════════════════════════════════════════════════
//
// La app estaba escrita en rioplatense con voseo: "Arrastrá tus fotos",
// "Miralá bien", "Elegí". Es una voz buena y era una decisión, no un
// descuido. Se cambió el 2026-08-25 por alcance: de ~600 millones de
// hispanohablantes, el voseo rioplatense es de unos 45.
//
// Neutro acá significa tres cosas concretas:
//
//   1. **Tuteo, no voseo.** "Arrastra", no "Arrastrá". "Elige", no
//      "Elegí". Y tampoco "usted": el registro sigue siendo cercano.
//   2. **Sin palabras que solo se usan en un país.** "En casa" y no
//      "Entrecasa"; "maleta" y no "valija".
//   3. **Cercano, no formal.** Neutro no es frío. "Sacaste una mejor" y
//      no "Se ha capturado una imagen de mayor calidad".
//
// ⚠️  LO QUE ESTE ARCHIVO NO PUEDE ARREGLAR. No existe una palabra que
//     sea la natural en los 20 países. "Zapatilla deportiva" es lo
//     normal en España y en México se dice "tenis". Cuando no hay
//     neutro real se elige la forma que MÁS gente entiende aunque no
//     sea la que usaría, y se anota acá abajo. Fingir que existe una
//     palabra perfecta sería peor que elegir una y decirlo.
//
// ═══════════════════════════════════════════════════════════════════
// LO QUE YA ESTABA BIEN Y NO SE TOCÓ
// ═══════════════════════════════════════════════════════════════════
//
// **La taxonomía de 108 prendas ya estaba en neutro.** Dice "Camiseta",
// "Falda", "Chaqueta", "Suéter", "Sudadera", "Calcetines", "Aretes",
// "Lentes de sol", "Maleta" — no "remera", "pollera", "campera",
// "buzo", "medias", "aros", "anteojos", "valija". Alguien hizo ese
// trabajo antes y no hacía falta rehacerlo. Se buscó a propósito antes
// de reescribir nada.
//
// Por eso este archivo es casi la identidad: la clave guardada y la
// etiqueta que se muestra coinciden en casi todos los casos. Eso NO lo
// vuelve inútil — es el archivo que le da sentido a `en.js`, y es donde
// se arreglan los tres casos de abajo.
//
// ═══════════════════════════════════════════════════════════════════
// LO QUE SÍ CAMBIA RESPECTO DE LO QUE SE MOSTRABA ANTES
// ═══════════════════════════════════════════════════════════════════
//
// Estas claves nacieron pegadas porque son CLAVES, y la app las venía
// mostrando crudas a la persona. No es una traducción: es un bug que se
// arregla de paso.
//
//     Azulclaro     → "Azul claro"
//     Azuloscuro    → "Azul oscuro"
//     Verdeclaro    → "Verde claro"
//     MarrónOscuro  → "Marrón oscuro"
//     Petroleo      → "Petróleo"        (le faltaba la tilde)
//     violeta       → "Violeta"         (arrancaba en minúscula)
//
// Y una sola palabra regional en los catálogos:
//
//     Entrecasa     → "En casa"
//
// ⚠️  LAS CLAVES DE LA IZQUIERDA NO SE TOCAN NUNCA. Están escritas en
//     filas de `prendas` y validadas por CHECK constraints. Cambiar una
//     rompe el armario de todos. Ver el encabezado de `src/i18n.js`.

const es = {
  catalogo: {
    familiaColor: {
      "Negros": "Negros",
      "Blancos": "Blancos",
      "Grises": "Grises",
      "Beige": "Beige",
      "Azules": "Azules",
      "Marrones": "Marrones",
      "Verdes": "Verdes",
      "Rojos": "Rojos",
      "Rosas": "Rosas",
      "Violetas": "Violetas",
      "Naranjas": "Naranjas",
      "Amarillos": "Amarillos",
    },
    subcategoria: {
      "Camisas y blusas": "Camisas y blusas",
      "Camisetas y polos": "Camisetas y polos",
      "Deporte": "Deporte",
      "Pantalones": "Pantalones",
      "Faldas": "Faldas",
      "Shorts y bermudas": "Shorts y bermudas",
      "Ropa de baño": "Ropa de baño",
      "Vestidos": "Vestidos",
      "Enterizos y monos": "Enterizos y monos",
      "Interior y descanso": "Interior y descanso",
      "Sastrería": "Sastrería",
      "Suéteres": "Suéteres",
      "Sudaderas": "Sudaderas",
      "Chaquetas": "Chaquetas",
      "Abrigos": "Abrigos",
      "Formal e intermedio": "Formal e intermedio",
      "Botas": "Botas",
      "Casual y deportivo": "Casual y deportivo",
      "Complementos de vestir": "Complementos de vestir",
      "Bolsos y equipaje": "Bolsos y equipaje",
      "Joyería y relojería": "Joyería y relojería",
      "Otros accesorios": "Otros accesorios",
    },
    tipo: {
      "Superior": "Superior",
      "Inferior": "Inferior",
      "Enterizo": "Enterizo",
      "Calzado": "Calzado",
      "Abrigo": "Abrigo",
      "Accesorios": "Accesorios",
    },
    color: {
      "Negro": "Negro",
      "Blanco": "Blanco",
      "Azul": "Azul",
      "Azulclaro": "Azul claro",
      "Azuloscuro": "Azul oscuro",
      "Rojo": "Rojo",
      "Verde": "Verde",
      "Verdeclaro": "Verde claro",
      "Gris": "Gris",
      "Beige": "Beige",
      "Marrón": "Marrón",
      "MarrónOscuro": "Marrón oscuro",
      "Naranja": "Naranja",
      "Amarillo": "Amarillo",
      "Morado": "Morado",
      "violeta": "Violeta",
      "Rosa": "Rosa",
      "Borgoña": "Borgoña",
      "Celeste": "Celeste",
      "Petroleo": "Petróleo",
    },
    material: {
      "Algodón": "Algodón",
      "Denim": "Denim",
      "Lana": "Lana",
      "Poliéster": "Poliéster",
      "Mezcla": "Mezcla",
      "Cuero": "Cuero",
      "Gamuza": "Gamuza",
      "Seda": "Seda",
      "Lino": "Lino",
    },
    estado: {
      "Nueva": "Nueva",
      "Buena": "Buena",
      "Usada": "Usada",
      "Gastada": "Gastada",
    },
    disponibilidad: {
      "Disponible": "Disponible",
      "En lavandería": "En lavandería",
      "En arreglo": "En arreglo",
      "Guardada": "Guardada",
    },
    ocasion: {
      "Deporte": "Deporte",
      "Estar en casa/Descanso": "Estar en casa/Descanso",
      "Uso diario": "Uso diario",
      "Trabajo formal": "Trabajo formal",
      "Noche/Fiestas/Clubbing": "Noche/Fiestas/Clubbing",
      "Corporativo formal": "Corporativo formal",
      "Etiqueta/Gala": "Etiqueta/Gala",
      "Trabajo casual": "Trabajo casual",
      "Ropa de trabajo": "Ropa de trabajo",
      "Videollamada": "Videollamada",
      "Clases": "Clases",
      "Juntada": "Juntada",
      "Trabajo": "Trabajo",
      "Trabajo día especial": "Trabajo día especial",
      "Noche": "Noche",
      "Casual": "Casual",
      "Entrecasa": "En casa",
      "Gym": "Gym",
      "Verano noche": "Verano noche",
      "Verano casual": "Verano casual",
    },
    zona: {
      "torso": "torso",
      "piernas": "piernas",
      "pies": "pies",
    },
    estacion: {
      "Primavera": "Primavera",
      "Verano": "Verano",
      "Otoño": "Otoño",
      "Invierno": "Invierno",
    },
    preset: {
      "Primavera-Verano": "Primavera-Verano",
      "Otoño-Invierno": "Otoño-Invierno",
      "Entretiempo": "Entretiempo",
      "Todo el año": "Todo el año",
    },
    prenda: {
      "Camisa de vestir": "Camisa de vestir",
      "Camisa casual": "Camisa casual",
      "Camisa estampada": "Camisa estampada",
      "Camisa oversize": "Camisa oversize",
      "Guayabera": "Guayabera",
      "Blusa elegante": "Blusa elegante",
      "Blusa casual": "Blusa casual",
      "Top sin mangas": "Top sin mangas",
      "Camiseta básica": "Camiseta básica",
      "Polo": "Polo",
      "Camiseta henley": "Camiseta henley",
      "Camiseta sin mangas": "Camiseta sin mangas",
      "Top deportivo": "Top deportivo",
      "Camiseta deportiva": "Camiseta deportiva",
      "Pantalón de vestir": "Pantalón de vestir",
      "Pantalón chino": "Pantalón chino",
      "Jeans": "Jeans",
      "Pantalón cargo": "Pantalón cargo",
      "Pantalón ancho": "Pantalón ancho",
      "Jogger": "Jogger",
      "Leggings": "Leggings",
      "Falda lápiz": "Falda lápiz",
      "Falda midi": "Falda midi",
      "Falda larga": "Falda larga",
      "Falda plisada": "Falda plisada",
      "Minifalda": "Minifalda",
      "Bermuda de vestir": "Bermuda de vestir",
      "Short casual": "Short casual",
      "Short deportivo": "Short deportivo",
      "Mallas de compresión": "Mallas de compresión",
      "Traje de baño tipo bermuda": "Traje de baño tipo bermuda",
      "Vestido de noche": "Vestido de noche",
      "Vestido de cóctel": "Vestido de cóctel",
      "Vestido de día": "Vestido de día",
      "Vestido blazer": "Vestido blazer",
      "Jumpsuit formal": "Jumpsuit formal",
      "Jumpsuit casual": "Jumpsuit casual",
      "Mono corto": "Mono corto",
      "Overol": "Overol",
      "Traje de baño entero": "Traje de baño entero",
      "Bikini": "Bikini",
      "Pijama": "Pijama",
      "Blazer estructurado": "Blazer estructurado",
      "Blazer casual": "Blazer casual",
      "Chaleco de vestir": "Chaleco de vestir",
      "Suéter cuello alto": "Suéter cuello alto",
      "Suéter cuello V o redondo": "Suéter cuello V o redondo",
      "Cárdigan": "Cárdigan",
      "Suéter oversize": "Suéter oversize",
      "Sudadera con capucha": "Sudadera con capucha",
      "Sudadera sin capucha": "Sudadera sin capucha",
      "Sudadera con cierre": "Sudadera con cierre",
      "Chaqueta de cuero": "Chaqueta de cuero",
      "Chaqueta de jeans": "Chaqueta de jeans",
      "Chaqueta bomber": "Chaqueta bomber",
      "Sobrecamisa": "Sobrecamisa",
      "Abrigo de lana": "Abrigo de lana",
      "Gabardina": "Gabardina",
      "Chaqueta acolchada": "Chaqueta acolchada",
      "Parka": "Parka",
      "Impermeable": "Impermeable",
      "Bata": "Bata",
      "Salida de baño": "Salida de baño",
      "Zapato Oxford o Derby": "Zapato Oxford o Derby",
      "Mocasín": "Mocasín",
      "Zapato de tacón": "Zapato de tacón",
      "Sandalia de vestir": "Sandalia de vestir",
      "Alpargata": "Alpargata",
      "Botín Chelsea": "Botín Chelsea",
      "Botín con cordones": "Botín con cordones",
      "Bota de vestir": "Bota de vestir",
      "Bota militar": "Bota militar",
      "Bota de lluvia": "Bota de lluvia",
      "Bota de nieve": "Bota de nieve",
      "Zapatilla urbana": "Zapatilla urbana",
      "Náutico": "Náutico",
      "Zapatilla deportiva": "Zapatilla deportiva",
      "Sandalia tipo slide": "Sandalia tipo slide",
      "Sandalia casual": "Sandalia casual",
      "Corbata": "Corbata",
      "Moño": "Moño",
      "Pañuelo de bolsillo": "Pañuelo de bolsillo",
      "Cinturón": "Cinturón",
      "Tirantes": "Tirantes",
      "Bolso de mano": "Bolso de mano",
      "Bolso cruzado": "Bolso cruzado",
      "Bolso tote": "Bolso tote",
      "Mochila urbana": "Mochila urbana",
      "Maletín": "Maletín",
      "Riñonera": "Riñonera",
      "Maleta de viaje": "Maleta de viaje",
      "Reloj formal": "Reloj formal",
      "Smartwatch": "Smartwatch",
      "Anillo": "Anillo",
      "Collar": "Collar",
      "Pulsera": "Pulsera",
      "Aretes": "Aretes",
      "Gemelos": "Gemelos",
      "Sombrero": "Sombrero",
      "Gorra": "Gorra",
      "Gorro de lana": "Gorro de lana",
      "Bufanda": "Bufanda",
      "Guantes": "Guantes",
      "Lentes de sol": "Lentes de sol",
      "Ropa interior": "Ropa interior",
      "Calcetines de vestir": "Calcetines de vestir",
      "Calcetines deportivos": "Calcetines deportivos",
      "Calcetines invisibles": "Calcetines invisibles",
    },
  },

  // El catálogo de estilos. `clave` ya estaba separada del texto en
  // `lib/estilos.js` desde el principio — es el único catálogo que
  // nació bien y sirvió de modelo para todos los demás.
  estilo: {
    deportivo: { nombre: "Deportivo", mensaje: "Cercano y sin esfuerzo" },
    tradicional: { nombre: "Tradicional", mensaje: "Confiable y serio" },
    elegante: { nombre: "Elegante", mensaje: "Sobrio y de calidad" },
    romantico: { nombre: "Romántico", mensaje: "Suave y cercano" },
    magnetico: { nombre: "Magnético", mensaje: "Presente y sensual" },
    creativo: { nombre: "Creativo", mensaje: "Original y propio" },
    dramatico: { nombre: "Dramático", mensaje: "Rotundo y actual" },
  },

  // Plurales. Español usa `one` y `other`; están las dos formas aunque
  // hoy la app arme el plural a mano en 18 lugares — este es el destino
  // de esos 18.
  /* Los errores del servidor. La clave es el código que manda el
   * backend ({codigo}); los {marcadores} se llenan desde `datos`.
   * En español coinciden con los textos que el servidor tenía antes
   * de hablar por códigos — nadie ve nada distinto. */
  error: {
    ACCOUNT_DELETE_FAILED: "No se pudo borrar tu cuenta. No se perdió nada: tu cuenta sigue ahí. Prueba de nuevo.",
    ACCOUNT_EXISTS: "Ya existe una cuenta con ese email.",
    AI_NOT_CONFIGURED: "El servidor no tiene configurada la clave de la IA",
    AI_UNAVAILABLE: "El análisis automático de fotos no está disponible en este momento.",
    BATCH_FAILED: "No se pudo procesar la tanda de fotos",
    BATCH_TOO_LARGE: "Máximo {max} fotos por tanda. Enviaste {enviadas}.",
    BODY_NOT_JSON: "El cuerpo del pedido no es JSON válido.",
    BODY_TOO_LARGE: "El contenido es demasiado grande para esta operación.",
    BOTH_GARMENTS_MISSING: "Faltan las dos prendas",
    CHOSEN_NOT_LIST: "Las prendas elegidas tienen que ser una lista",
    COLORS_LOAD_FAILED: "No se pudieron cargar los colores",
    COLOR_BAD_HEX: "El color tiene que ser un hexadecimal de seis dígitos, como #C8322F.",
    COLOR_CREATE_FAILED: "No se pudo crear el color",
    COLOR_DELETE_FAILED: "No se pudo borrar el color",
    COLOR_EXISTS_IN_LIST: "“{nombre}” ya existe en la lista.",
    COLOR_IN_USE: "“{nombre}” está en uso en {n} prenda(s).",
    COLOR_LIMIT_REACHED: "Llegaste al máximo de {max} colores propios.",
    COLOR_NAME_MISSING: "Falta el nombre del color",
    COLOR_NOT_FOUND: "Ese color no existe",
    DAILY_PHOTO_LIMIT: "Llegaste al máximo de fotos por hoy. Lo que ya subiste está guardado — puedes seguir mañana, o cargar prendas a mano.",
    DATE_INVALID: "Fecha inválida",
    DATE_IN_FUTURE: "La fecha no puede ser futura",
    DAYS_NOT_OBJECT: "Los días por ocasión tienen que ser un objeto",
    DAYS_PER_OCCASION_MISSING: "Falta indicar cuántos días de cada ocasión",
    DUPLICATE_COLOR: "Ya tienes un color “{nombre}”.",
    DUPLICATE_NAME: "Ya existe algo con ese nombre.",
    DUPLICATE_OCCASION: "Ya tienes una ocasión “{nombre}”.",
    EMAIL_INVALID: "El email no parece válido.",
    EMAIL_MISSING: "Falta el email",
    EMAIL_NOT_CONFIRMED: "Tienes que confirmar tu email antes de entrar. Revisa tu correo.",
    EMAIL_PASSWORD_REQUIRED: "Email y contraseña son obligatorios",
    EMAIL_RATE_LIMIT: "Se alcanzó el límite de correos. Espera unos minutos y prueba de nuevo.",
    EMAIL_SEND_FAILED: "No pudimos enviar el correo. Es un problema nuestro, no tuyo. Prueba de nuevo en un rato.",
    FIELD_INVALID: "Valor inválido en {campo}.",
    GARMENT_LIMIT_REACHED: "Durante la prueba puedes tener hasta {max} prendas. Con una suscripción no hay límite.",
    FORMALITY_MISSING: "Falta el nivel de formalidad. Sin eso la regla de estilo no puede evaluar la prenda.",
    GARMENTS_SAVE_FAILED: "No se pudieron guardar las prendas",
    GARMENT_DELETE_FAILED: "No se pudo borrar la prenda",
    GARMENT_EDIT_FAILED: "No se pudo editar la prenda",
    GARMENT_LIST_MISSING: "Falta la lista de prendas",
    GARMENT_LOAD_FAILED: "No se pudo cargar la prenda",
    GARMENT_NOT_FOUND: "Esa prenda no existe",
    GARMENT_USES_FAILED: "No pudimos cargar cuándo usaste esta prenda.",
    HISTORY_LOAD_FAILED: "No se pudo cargar el historial",
    IMAGE_EMPTY: "La imagen vino vacía",
    INVALID_CREDENTIALS: "Email o contraseña incorrectos.",
    LOGIN_FAILED: "No se pudo iniciar sesión",
    NOTHING_TO_CHANGE: "No hay nada que cambiar",
    NO_CHANGES_SENT: "No mandaste ningún cambio",
    NO_GARMENTS_SENT: "No mandaste ninguna prenda",
    NO_PHOTOS_SENT: "No mandaste ninguna foto",
    OCCASIONS_LOAD_FAILED: "No se pudieron cargar las ocasiones",
    OCCASION_CREATE_FAILED: "No se pudo crear la ocasión",
    OCCASION_DELETE_FAILED: "No se pudo borrar la ocasión",
    OCCASION_EXISTS_IN_LIST: "“{nombre}” ya existe en la lista.",
    OCCASION_IN_USE: "“{nombre}” está en uso en {n} prenda(s).",
    OCCASION_LEVEL_RANGE: "El nivel tiene que ser un número entero de {min} a {max}.",
    OCCASION_LIMIT_REACHED: "Llegaste al máximo de {max} ocasiones propias.",
    OCCASION_LIST_ALREADY_SET: "Tu lista ya está armada. Puedes crear o borrar ocasiones desde el perfil.",
    OCCASION_LIST_BUILD_FAILED: "No se pudo armar tu lista de ocasiones",
    OCCASION_NAME_MISSING: "Falta el nombre de la ocasión",
    OCCASION_NOT_FOUND: "Esa ocasión no existe",
    OPERATION_FAILED: "No se pudo completar la operación. Prueba de nuevo.",
    ORIGIN_NOT_ALLOWED: "Origen no permitido",
    PERFUME_NOT_FOUND: "Ese perfume no existe.",
    PERFUME_NAME_MISSING: "Escribe el nombre del perfume.",
    PERFUME_FIELD_INVALID: "Un dato del perfume no es válido ({campo}).",
    PERFUME_SAVE_FAILED: "No se pudo guardar el perfume.",
    PERFUME_LOAD_FAILED: "No se pudieron cargar tus perfumes.",
    PERFUME_DELETE_FAILED: "No se pudo borrar el perfume.",
    PERFUME_AI_FAILED: "La IA no pudo contestar ahora. Puedes completar los datos a mano.",
    OUTFIT_EDIT_FAILED: "No se pudo corregir el registro",
    OUTFIT_LOG_FAILED: "No se pudo registrar el outfit",
    OUTFIT_NOT_FOUND: "Ese registro no existe.",
    PASSWORD_NEEDS_REAUTH: "Por tu seguridad, confirma tu identidad antes de cambiar la contraseña. Pide un link nuevo desde el login.",
    PASSWORD_CHANGE_FAILED: "No se pudo cambiar la contraseña",
    PASSWORD_TOO_SHORT: "La contraseña tiene que tener al menos 8 caracteres",
    PLAN_EXPIRED: "Tu plan terminó. Sigues viendo, editando y registrando tu armario — para agregar prendas nuevas necesitas una suscripción.",
    PHOTO_DISCARD_FAILED: "No se pudo descartar la foto",
    PHOTO_IN_USE: "Esa foto está en uso por una prenda",
    PHOTO_NOT_PROCESSED: "Esta foto quedó fuera de la tanda. Prueba de nuevo con ella sola.",
    PHOTO_NOT_YOURS: "Esa foto no es tuya",
    PHOTO_PATH_MISSING: "Falta foto_path",
    PHOTO_UPLOAD_FAILED: "No se pudo subir la foto",
    PROFILE_LOAD_FAILED: "No se pudo cargar el perfil",
    PROFILE_MISSING: "Tu usuario existe pero no tiene perfil. Revisa el trigger al_crear_usuario en Supabase.",
    PROFILE_SAVE_FAILED: "No se pudo guardar el perfil",
    RECORD_DELETE_FAILED: "No se pudo borrar el registro",
    REFERENCE_MISSING: "Eso hace referencia a algo que ya no existe.",
    REFRESH_TOKEN_MISSING: "Falta el refresh token",
    REQUIRED_FIELD_MISSING: "Falta un dato obligatorio.",
    SAME_GARMENT: "Son la misma prenda",
    SAVE_FAILED: "No se pudo guardar",
    SERVER_ERROR: "Algo salió mal en el servidor",
    SESSION_INVALID: "Sesión inválida o vencida",
    SESSION_REFRESH_FAILED: "No se pudo renovar la sesión",
    SESSION_TOKEN_MISSING: "Falta el token de sesión",
    SESSION_VERIFY_FAILED: "No se pudo verificar la sesión",
    SHOES_LIMIT_RANGE: "El tope de calzados va de 1 a 6",
    SIGNUP_FAILED: "No se pudo completar el registro",
    SUGGESTIONS_FAILED: "No se pudieron armar las sugerencias",
    SUITCASE_BUILD_FAILED: "No se pudo armar la valija",
    SUITCASE_DELETE_FAILED: "No se pudo borrar la valija",
    SUITCASE_LIST_INVALID: "La lista de la valija no tiene la forma esperada, o es demasiado grande",
    SUITCASE_NOT_OBJECT: "La valija tiene que ser un objeto",
    SUITCASE_READ_FAILED: "No se pudo leer la valija guardada",
    SUITCASE_SAVE_FAILED: "No se pudo guardar la valija",
    THUMBNAIL_NEEDS_PHOTO: "La miniatura se cambia junto con la foto, no por separado",
    TOO_MANY_GARMENTS: "Máximo {max} prendas por vez.",
    TOO_MANY_REQUESTS: "Demasiados intentos. Prueba de nuevo en {minutos} min.",
    TRIP_TOO_LONG: "El máximo son {max} días. Más que eso ya no es una valija.",
    TRIP_ZERO_DAYS: "El viaje tiene cero días",
    UNKNOWN_FAMILY: "Familia de color desconocida: {valor}.",
    UNKNOWN_OCCASION: "Ocasión desconocida: {valor}.",
    UNKNOWN_REASON: "Motivo desconocido: {valor}.",
    UNKNOWN_SEASON: "Estación desconocida: {valor}.",
    UNKNOWN_TEMPERATURE: "Temperatura desconocida: {valor}.",
    UNSUPPORTED_IMAGE_TYPE: "Tipo de imagen no soportado. Solo {tipos}.",
    VALUE_BAD_FORMAT: "Alguno de los valores no tiene el formato esperado.",
    VETOES_LOAD_FAILED: "No se pudieron cargar los vetos",
    VETO_DELETE_FAILED: "No se pudo borrar el veto",
    VETO_SAVE_FAILED: "No se pudo guardar el veto",
    WARDROBE_ANALYSIS_FAILED: "No se pudo analizar el armario",
    WARDROBE_LOAD_FAILED: "No se pudo cargar el armario",
  },

  plural: {
  /* ⚠️  LA FORMA `many` NO ES DECORATIVA, Y CASI SE OLVIDA.
   *
   * El español tiene TRES categorías de plural en CLDR, no dos:
   * `one`, `other` y `many`. La tercera se usa para los millones
   * redondos, donde la lengua pide un "de": "un millón DE prendas".
   *
   * Nadie va a tener un millón de prendas. Se declara igual porque
   * `test_i18n.js` le pregunta a `Intl.PluralRules` qué formas usa
   * este idioma, y no al criterio de quien escribe el archivo. El
   * test lo encontró en es, pt y fr después de haberlos dado por
   * terminados. */
    prendas: { one: "{n} prenda", other: "{n} prendas", many: "{n} de prendas" },
    combinaciones: { one: "{n} combinación", other: "{n} combinaciones", many: "{n} de combinaciones" },
    outfits: { one: "{n} outfit", other: "{n} outfits", many: "{n} de outfits" },
    listas: { one: "{n} lista", other: "{n} listas", many: "{n} de listas" },
    veces: {one: "{n} vez", other: "{n} veces", many: "{n} de veces"},
    cambios: {one: "Guardar {n} cambio", other: "Guardar {n} cambios", many: "Guardar {n} de cambios"},
    opciones: {one: "{n} opción", other: "{n} opciones", many: "{n} de opciones"},
    diasConRegistro: {one: "{registrados} de {n} día con registro", other: "{registrados} de {n} días con registro", many: "{registrados} de {n} de días con registro"},
    prendasFaltan: {one: "{n} prenda de este outfit ya no está en tu armario", other: "{n} prendas de este outfit ya no están en tu armario", many: "{n} de prendas de este outfit ya no están en tu armario"},
    outfitsRegistrados: {one: "outfit registrado", other: "outfits registrados", many: "de outfits registrados"},
    diasValija: {one: "Armar valija para {n} día", other: "Armar valija para {n} días", many: "Armar valija para {n} de días"},
    diasSinResolver: {one: "{n} día sin resolver.", other: "{n} días sin resolver.", many: "{n} de días sin resolver."},
  },
};

export default es;
